import type { GetChannelsMessagesByCidItem } from "@birdwatch/client";
import { cn } from "cn";
import { isEmpty } from "lodash-es";
import { useMemo, useState } from "react";

import { Attachment } from "@/components/ui/attachment-new";
import { LightBox } from "@/components/ui/lightbox";
import { isAttachmentUploaded } from "@/libs/attachment-visibility";
import {
  isImage,
  isVideo,
  resolveAttachmentFileExtension,
} from "@/libs/file-helper";

export type MessageAttachmentProps = {
  attachments: GetChannelsMessagesByCidItem["attachments"];
};

type AttachmentItem = NonNullable<
  NonNullable<MessageAttachmentProps["attachments"]>[number]
>;

/**
 * How the tiles lay out: one full-width preview, a fixed pair, or the wrapping grid.
 *
 * A pair is always two columns, never three — two tiles in a three-column grid would take
 * a third of the bubble each and leave the last column empty, which reads as a missing
 * third picture rather than as a pair.
 */
function mediaLayoutClassName(isSingleMedia: boolean, count: number): string {
  if (isSingleMedia) return "block";
  if (count === 2) return "grid grid-cols-2";
  return "grid grid-cols-2 @md/message-media:grid-cols-3";
}

/**
 * Is this message's attachment list the lone-image-or-video case?
 *
 * The extension resolves through `resolveAttachmentFileExtension`, the same order the
 * attachment tile reads it in — name first, then the URI. Reading only the URI
 * missed every CDN link served without one, and the lone photo then quietly took a
 * square half-column instead of its 16:9 preview.
 */
function isSingleMediaAttachment(
  attachments: MessageAttachmentProps["attachments"],
): boolean {
  const uploaded = attachments?.filter((attachment: AttachmentItem) =>
    isAttachmentUploaded(attachment),
  );

  if (uploaded?.length !== 1) return false;

  const fileExtension = resolveAttachmentFileExtension({
    fileName: uploaded[0]?.fileName,
    contentUri: uploaded[0]?.contentUri,
  });
  return isImage(fileExtension) || isVideo(fileExtension);
}

export function MessageAttachment({ attachments }: MessageAttachmentProps) {
  const [attachmentIndex, setAttachmentIndex] = useState<number | undefined>(
    undefined,
  );
  // A message can be sent while an attachment is still uploading — those rows are not
  // renderable yet. See `isAttachmentUploaded` for why `contentUri` cannot decide this.
  const attachmentsFiltered = useMemo(() => {
    return attachments?.filter((attachment: AttachmentItem) =>
      isAttachmentUploaded(attachment),
    );
  }, [attachments]);

  const isOnlyOneMedia = isSingleMediaAttachment(attachments);

  if (isEmpty(attachmentsFiltered)) {
    return null;
  }

  return (
    // `@container/message-media` so the grid counts its columns against THIS width — the
    // bubble's, which is what the reader sees — rather than the viewport's. A thread
    // shares its panel with the order sidebar and is routinely far narrower than the
    // window, so a viewport breakpoint answered the wrong question.
    //
    // `container-type: inline-size` zeroes an element's intrinsic width, and `w-full` is
    // a percentage, so BOTH need a parent with a definite width. That is why
    // `conversation-message-item` counts media in `hasFullWidthContent`: a bubble that
    // hugs its content would measure nothing here and collapse the previews. The classic
    // row and the pinned strip are block-level and already definite.
    <div className="@container/message-media mt-1 w-full">
      <div
        className={cn(
          /* sorted */
          // Three or more go two per row, and three per row once the bubble is at least
          // 448px wide — a 496px thread bubble takes three, a narrow panel keeps two.
          mediaLayoutClassName(
            isOnlyOneMedia,
            attachmentsFiltered?.length ?? 0,
          ),
          "gap-1",
        )}
      >
        {attachmentsFiltered?.map(
          (attachment: AttachmentItem, index: number) => {
            const contentUri = attachment.contentUri ?? "";
            return (
              <Attachment
                key={attachment?.id ?? index}
                fileName={attachment?.fileName || ""}
                originalContentUri={contentUri}
                thumbnailUri={attachment?.thumbnailUri}
                // The column decides the width; the ratio keeps the shape, so the
                // picture scales whole and is cropped by `object-cover`.
                ratio={isOnlyOneMedia ? "video" : "square"}
                // 8px, the same corner the bubble's cards and document rows are cut at.
                previewClassName="rounded-lg"
                onClick={() => {
                  setAttachmentIndex(index);
                }}
              />
            );
          },
        )}
      </div>

      {/* Outside the grid: an open lightbox must never take a column of its own. */}
      <LightBox
        open={attachmentIndex !== undefined}
        slideIndex={attachmentIndex}
        attachments={attachmentsFiltered ?? []}
        onClose={() => setAttachmentIndex(undefined)}
      />
    </div>
  );
}
